/**
 * Lightweight client-side CSV parser for preview purposes only.
 * The real validation happens on the backend — this is just for the UI preview table.
 */

export interface CsvPreviewRow {
  rowNumber: number;
  /** Raw cell values, keyed by detected header name */
  [key: string]: string | number;
}

export interface CsvParseResult {
  headers: string[];
  rows: CsvPreviewRow[];
  totalRows: number;
}

/**
 * Parse a CSV file and return the first `maxPreviewRows` rows.
 * Handles quoted fields and common line endings (CRLF, LF).
 */
export async function parseCSVForPreview(
  file: File,
  maxPreviewRows = 50
): Promise<CsvParseResult> {
  const text = await file.text();
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  const headers = parseCsvLine(lines[0]);
  const rows: CsvPreviewRow[] = [];

  const dataLines = lines.slice(1);
  const previewCount = Math.min(dataLines.length, maxPreviewRows);

  for (let i = 0; i < previewCount; i++) {
    const values = parseCsvLine(dataLines[i]);
    const row: CsvPreviewRow = { rowNumber: i + 2 }; // +2: 1-indexed, skip header
    headers.forEach((header, idx) => {
      row[header] = values[idx]?.trim() ?? "";
    });
    rows.push(row);
  }

  return { headers, rows, totalRows: dataLines.length };
}

/** Parse a single CSV line, respecting quoted fields */
function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote ""
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // skip next quote
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }

  result.push(current.trim());
  return result;
}
